import {Component, OnInit} from '@angular/core';
import {Router} from "@angular/router";
import {MsalService} from "@azure/msal-angular";
import {EventType} from "@azure/msal-browser";
import {timeout} from "rxjs/operators";
import {NgxSpinnerService} from "ngx-spinner";
import {Roles} from "../../role-based.guard";
import {AuthService} from "../../services/auth-service.service";
import {SessionStateService} from "../../services/session-state.service";
import {UserService} from "../../services/user.service";

/**
 * Where B2C returns every interactive sign-in (`redirectUri` in app.module.ts), and the one place
 * that decides which page a user starts on.
 *
 * Because it is the only funnel for a fresh sign-in, it is also where a deactivated account has to
 * be caught. Letting the shell load first and reacting to the 401 afterwards works, but the user
 * watches a page they are not allowed to see appear and then disappear.
 */
@Component({
  selector: 'app-authredirect',
  templateUrl: './authredirect.component.html',
  styleUrls: ['./authredirect.component.scss']
})
export class AuthredirectComponent implements OnInit {
  /**
   * MSAL can settle the redirect either before or after this component initialises, so both paths
   * below have to be armed - but only the first one to arrive may route.
   */
  private routed = false;

  /**
   * Last resort. If MSAL never reports the redirect as finished, this page would otherwise sit on
   * "Redirecting..." for ever with no way out - which is exactly what a stranded user reports.
   */
  private static readonly REDIRECT_SETTLED_BY_MS = 8000;

  /** Sign-in must not hang on the access check either; on timeout we let the landing page load. */
  private static readonly ACCESS_CHECK_TIMEOUT_MS = 10000;

  constructor(private router: Router,
              private msalService: MsalService,
              private authService: AuthService,
              private sessionState: SessionStateService,
              private spinner: NgxSpinnerService,
              private userService: UserService) {}

  ngOnInit(): void {
    // Returning from B2C lands here on a blank page while MSAL settles the redirect and the access
    // check runs. Without the overlay that reads as the app having stalled.
    this.spinner.show();

    this.msalService.instance.addEventCallback((event) => {
      if (event.eventType === EventType.HANDLE_REDIRECT_END) {
        // Redirect processing has finished, so whatever accounts exist now is the final answer.
        this.route(true);
      }
    });

    // Already signed in when we got here - no redirect event will arrive.
    this.route(false);

    setTimeout(() => this.route(true), AuthredirectComponent.REDIRECT_SETTLED_BY_MS);
  }

  /**
   * @param redirectSettled whether MSAL has finished processing the redirect. Until it has, an
   * empty account list means "still working", not "not signed in" - treating the two the same
   * leaves the user stranded on this page, because the real answer arrives later and finds the
   * routing already spent.
   */
  private route(redirectSettled: boolean): void {
    if (this.routed) {
      return;
    }

    if (this.msalService.instance.getAllAccounts().length === 0) {
      if (redirectSettled) {
        this.routed = true;
        this.spinner.hide();
        this.router.navigate(['/login']);
      }
      return;
    }

    this.routed = true;

    // Marks the window in which a 401 means "you were never let in", so SessionTerminationService
    // sends the user to /account-deactivated rather than raising a dialog over the shell.
    this.sessionState.beginSignIn();

    this.userService
      .portalAccess()
      .pipe(timeout(AuthredirectComponent.ACCESS_CHECK_TIMEOUT_MS))
      .subscribe({
      next: () => {
        this.sessionState.endSignIn();
        this.routeToLanding();
      },
      error: () => {
        this.sessionState.endSignIn();
        this.spinner.hide();

        // A deactivated account has already been sent to its own page by the interceptor. Anything
        // else - the API being down, say - must not strand the user on "Redirecting...", so carry
        // on as normal and let the destination page report its own failure.
        if (!this.sessionState.isTerminating) {
          this.routeToLanding();
        }
      }
    });
  }

  private routeToLanding(): void {
    this.spinner.hide();

    const activeAccount = this.msalService.instance.getActiveAccount();

    if (activeAccount?.idTokenClaims?.['extension_CrmPassportVerified'] !== true) {
      this.router.navigate(['/verify-passport']);
      return;
    }

    // Send external users straight to their own landing page. Routing everyone to /organization
    // and letting RoleBasedGuard bounce them costs an extra page load and a visible flicker.
    if (this.authService.getUserRole().includes(Roles.EXTERNAL)) {
      this.router.navigate(['/external-contact']);
      return;
    }

    this.router.navigate(['/organization']);
  }
}
